// =====================================================
// FuMind_GM HR Module - Leave Management Service
// Phase LM-1 Final Frontend Integration
// Backend: Power Automate + SharePoint
// Flows:
// 1. F_HR_LeaveManagement_EnsureLeaveBalances
// 2. F_HR_LeaveManagement_GetData
// 3. F_HR_LeaveManagement_Action
// =====================================================

// TODO: Paste the HTTP POST URL from F_HR_LeaveManagement_EnsureLeaveBalances.
const HR_LEAVE_ENSURE_BALANCES_FLOW_URL = "/tracka-flow/v1/HR_LEAVE_ENSURE_BALANCES";

// HTTP POST URL from F_HR_LeaveManagement_GetData.
const HR_LEAVE_GETDATA_FLOW_URL = "/tracka-flow/v1/HR_LEAVE_GETDATA";

// HTTP POST URL from F_HR_LeaveManagement_Action.
const HR_LEAVE_ACTION_FLOW_URL = "/tracka-flow/v1/HR_LEAVE_ACTION";

const API_KEY = "";

// =====================================================
// Common helpers
// =====================================================
async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text, rawText: text };
  }
}

function normalizePowerAutomateResponse(rawData) {
  return rawData?.body || rawData || {};
}

function parseFlowArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}
function throwIfFlowFailed(response, data, fallbackMessage) {
  if (!response.ok || data?.success === false) {
    const flowMessage = data?.message || data?.error?.message || data?.rawText || "";
    const statusText = `${response.status} ${response.statusText}`.trim();
    throw new Error(flowMessage ? `${fallbackMessage}: ${flowMessage}` : `${fallbackMessage}. HTTP ${statusText}`);
  }
}

function ensureFlowUrl(url, label) {
  if (!url || url.includes("PASTE_")) {
    throw new Error(`${label} flow URL not configured. Paste the Power Automate HTTP POST URL in leaveManagementService.js.`);
  }
}

function extractChoiceValue(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(extractChoiceValue).filter(Boolean).join("; ");
  if (typeof value === "object") return extractChoiceValue(value.Value || value.value || value.Title || value.Email || value.DisplayName || "");
  return String(value);
}

function getValue(record = {}, ...names) {
  for (const name of names) {
    if (record?.[name] !== undefined && record?.[name] !== null) return record[name];
  }
  const normalized = Object.keys(record || {}).reduce((acc, key) => {
    acc[String(key).toLowerCase().replace(/[^a-z0-9]/g, "")] = record[key];
    return acc;
  }, {});
  for (const name of names) {
    const key = String(name).toLowerCase().replace(/[^a-z0-9]/g, "");
    if (normalized[key] !== undefined && normalized[key] !== null) return normalized[key];
  }
  return "";
}

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function isTruthy(value) {
  return value === true || value === "true" || value === "True" || value === "Yes" || value === "yes" || value === 1 || value === "1";
}
function normalizeLeaveStatus(value, fallback = "Pending") {
  const text = extractChoiceValue(value).trim().toLowerCase().replace(/\s+/g, " ");
  const labels = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    cancelled: "Cancelled",
    "cancellation requested": "Cancellation Requested",
    "cancelled after approval": "Cancelled After Approval",
  };
  return labels[text] || extractChoiceValue(value).trim() || fallback;
}

export function normalizeAssignment(record = {}) {
  return {
    employeeCode: extractChoiceValue(getValue(record, "employeeCode", "EmployeeCode")),
    leavePolicyId: extractChoiceValue(getValue(record, "leavePolicyId", "LeavePolicyId")),
    LeavePolicyId: extractChoiceValue(getValue(record, "LeavePolicyId", "leavePolicyId")),
    attendancePolicyId: extractChoiceValue(getValue(record, "attendancePolicyId", "AttendancePolicyId")),
    AttendancePolicyId: extractChoiceValue(getValue(record, "AttendancePolicyId", "attendancePolicyId")),
    holidayCalendarId: extractChoiceValue(getValue(record, "holidayCalendarId", "HolidayCalendarId")),
    weeklyOffPatternId: extractChoiceValue(getValue(record, "weeklyOffPatternId", "WeeklyOffPatternId")),
    legalEntityCode: extractChoiceValue(getValue(record, "legalEntityCode", "LegalEntityCode")),
    locationCode: extractChoiceValue(getValue(record, "locationCode", "LocationCode")),
    departmentCode: extractChoiceValue(getValue(record, "departmentCode", "DepartmentCode")),
    workUnitCode: extractChoiceValue(getValue(record, "workUnitCode", "WorkUnitCode")),
    assignmentId: extractChoiceValue(getValue(record, "assignmentId", "AssignmentId", "ID")),
    source: extractChoiceValue(getValue(record, "source", "Source")) || "EnsureLeaveBalances flow",
  };
}

export function normalizeLeavePolicyRule(record = {}) {
  return {
    id: String(getValue(record, "id", "ID", "ItemInternalId") || ""),
    ID: String(getValue(record, "ID", "id", "ItemInternalId") || ""),
    leavePolicyId: extractChoiceValue(getValue(record, "LeavePolicyId", "leavePolicyId")),
    leaveType: extractChoiceValue(getValue(record, "LeaveType", "leaveType", "LeaveTypeCode", "LeaveCode")),
    leaveCategory: extractChoiceValue(getValue(record, "LeaveCategory", "leaveCategory")),
    entitledDays: toNumber(getValue(record, "EntitledDays", "entitledDays", "EntitlementDays", "AnnualEntitlement", "DaysPerYear"), 0),
    accrualMethod: extractChoiceValue(getValue(record, "AccrualMethod", "accrualMethod")),
    carryForwardAllowed: isTruthy(getValue(record, "CarryForwardAllowed", "carryForwardAllowed")),
    maxCarryForwardDays: toNumber(getValue(record, "MaxCarryForwardDays", "maxCarryForwardDays"), 0),
    maxConsecutiveDays: toNumber(getValue(record, "MaxConsecutiveDays", "maxConsecutiveDays"), 0),
    requiresProof: isTruthy(getValue(record, "RequiresProof", "requiresProof")),
    allowHalfDay: isTruthy(getValue(record, "AllowHalfDay", "allowHalfDay")),
    allowNegativeBalance: isTruthy(getValue(record, "AllowNegativeBalance", "allowNegativeBalance")),
    isPaid: getValue(record, "IsPaid", "isPaid") === "" ? true : isTruthy(getValue(record, "IsPaid", "isPaid")),
    isActive: getValue(record, "IsActive", "isActive") === "" ? true : isTruthy(getValue(record, "IsActive", "isActive")),
  };
}

export function normalizeLeaveRequest(record = {}) {
  return {
    id: String(getValue(record, "id", "ID", "ItemInternalId") || ""),
    ID: String(getValue(record, "ID", "id", "ItemInternalId") || ""),
    title: extractChoiceValue(getValue(record, "Title")),
    employeeCode: extractChoiceValue(getValue(record, "EmployeeCode", "employeeCode")),
    employeeName: extractChoiceValue(getValue(record, "EmployeeName", "employeeName")),
    leavePolicyId: extractChoiceValue(getValue(record, "LeavePolicyId", "leavePolicyId")),
    leaveType: extractChoiceValue(getValue(record, "LeaveType", "leaveType")),
    leaveCategory: extractChoiceValue(getValue(record, "LeaveCategory", "leaveCategory")),
    fromDate: getValue(record, "FromDate", "fromDate") || "",
    toDate: getValue(record, "ToDate", "toDate") || "",
    requestedDays: toNumber(getValue(record, "RequestedDays", "requestedDays", "Days"), 0),
    chargeableDays: toNumber(getValue(record, "ChargeableDays", "chargeableDays", "RequestedDays", "requestedDays", "Days"), 0),
    days: toNumber(getValue(record, "ChargeableDays", "chargeableDays", "RequestedDays", "requestedDays", "Days"), 0),
    reason: extractChoiceValue(getValue(record, "Reason", "reason")),
    status: normalizeLeaveStatus(getValue(record, "Status", "status")),
    Status: normalizeLeaveStatus(getValue(record, "Status", "status")),
    appliedOn: getValue(record, "AppliedOn", "appliedOn") || "",
    appliedBy: extractChoiceValue(getValue(record, "AppliedBy", "appliedBy")),
    actionedOn: getValue(record, "ActionedOn", "actionedOn") || "",
    actionedBy: extractChoiceValue(getValue(record, "ActionedBy", "actionedBy")),
    actionRemarks: extractChoiceValue(getValue(record, "ActionRemarks", "actionRemarks")),
    cancellationRequestedOn: getValue(record, "CancellationRequestedOn", "cancellationRequestedOn") || "",
    cancellationRequestedBy: extractChoiceValue(getValue(record, "CancellationRequestedBy", "cancellationRequestedBy")),
    cancellationActionedOn: getValue(record, "CancellationActionedOn", "cancellationActionedOn") || "",
    cancellationActionedBy: extractChoiceValue(getValue(record, "CancellationActionedBy", "cancellationActionedBy")),
    cancellationRejectedOn: getValue(record, "CancellationRejectedOn", "cancellationRejectedOn") || "",
    cancellationReason: extractChoiceValue(getValue(record, "CancellationReason", "cancellationReason")),
    legalEntityCode: extractChoiceValue(getValue(record, "LegalEntityCode", "legalEntityCode")),
    locationCode: extractChoiceValue(getValue(record, "LocationCode", "locationCode")),
    departmentCode: extractChoiceValue(getValue(record, "DepartmentCode", "departmentCode")),
    workUnitCode: extractChoiceValue(getValue(record, "WorkUnitCode", "workUnitCode")),
    holidayCalendarId: extractChoiceValue(getValue(record, "HolidayCalendarId", "holidayCalendarId")),
    weeklyOffPatternId: extractChoiceValue(getValue(record, "WeeklyOffPatternId", "weeklyOffPatternId")),
    assignmentId: extractChoiceValue(getValue(record, "AssignmentId", "assignmentId")),
    isActive: getValue(record, "IsActive", "isActive") === "" ? true : isTruthy(getValue(record, "IsActive", "isActive")),
    created: getValue(record, "Created", "created") || "",
    modified: getValue(record, "Modified", "modified") || "",
  };
}

export function normalizeLeaveBalance(record = {}) {
  const entitled = toNumber(getValue(record, "EntitledDays", "entitledDays", "entitled"), 0);
  const opening = toNumber(getValue(record, "OpeningBalance", "openingBalance"), 0);
  const accrued = toNumber(getValue(record, "AccruedDays", "accruedDays"), 0);
  const used = toNumber(getValue(record, "UsedDays", "usedDays", "used"), 0);
  const pending = toNumber(getValue(record, "PendingDays", "pendingDays", "pending"), 0);
  const adjusted = toNumber(getValue(record, "AdjustedDays", "adjustedDays"), 0);
  const carryForward = toNumber(getValue(record, "CarryForwardDays", "carryForwardDays"), 0);
  const calculatedAvailable = entitled + opening + accrued + adjusted + carryForward - used - pending;
  const available = toNumber(getValue(record, "AvailableDays", "availableDays", "available"), calculatedAvailable);
  return {
    id: String(getValue(record, "id", "ID", "ItemInternalId") || ""),
    ID: String(getValue(record, "ID", "id", "ItemInternalId") || ""),
    title: extractChoiceValue(getValue(record, "Title")),
    employeeCode: extractChoiceValue(getValue(record, "EmployeeCode", "employeeCode")),
    leavePolicyId: extractChoiceValue(getValue(record, "LeavePolicyId", "leavePolicyId")),
    leaveType: extractChoiceValue(getValue(record, "LeaveType", "leaveType")),
    leaveCategory: extractChoiceValue(getValue(record, "LeaveCategory", "leaveCategory")),
    leaveYear: toNumber(getValue(record, "LeaveYear", "leaveYear"), new Date().getFullYear()),
    entitledDays: entitled,
    entitled,
    openingBalance: opening,
    accruedDays: accrued,
    usedDays: used,
    used,
    pendingDays: pending,
    pending,
    adjustedDays: adjusted,
    carryForwardDays: carryForward,
    availableDays: available,
    available,
    lastCalculatedOn: getValue(record, "LastCalculatedOn", "lastCalculatedOn") || "",
    isActive: getValue(record, "IsActive", "isActive") === "" ? true : isTruthy(getValue(record, "IsActive", "isActive")),
  };
}

export function normalizeLeaveActivityLog(record = {}) {
  return {
    id: String(getValue(record, "id", "ID", "ItemInternalId") || ""),
    ID: String(getValue(record, "ID", "id", "ItemInternalId") || ""),
    title: extractChoiceValue(getValue(record, "Title")),
    leaveRequestId: extractChoiceValue(getValue(record, "LeaveRequestId", "leaveRequestId")),
    employeeCode: extractChoiceValue(getValue(record, "EmployeeCode", "employeeCode")),
    leaveType: extractChoiceValue(getValue(record, "LeaveType", "leaveType")),
    actionType: extractChoiceValue(getValue(record, "ActionType", "actionType")),
    previousStatus: extractChoiceValue(getValue(record, "PreviousStatus", "previousStatus")),
    newStatus: extractChoiceValue(getValue(record, "NewStatus", "newStatus")),
    days: toNumber(getValue(record, "Days", "days"), 0),
    actionBy: extractChoiceValue(getValue(record, "ActionBy", "actionBy")),
    actionByRole: extractChoiceValue(getValue(record, "ActionByRole", "actionByRole")),
    actionOn: getValue(record, "ActionOn", "actionOn") || "",
    remarks: extractChoiceValue(getValue(record, "Remarks", "remarks")),
    sourceModule: extractChoiceValue(getValue(record, "SourceModule", "sourceModule")) || "LeaveManagement",
  };
}

// =====================================================
// Ensure Leave Balances
// =====================================================
export async function ensureLeaveBalances({ employeeCode, leaveYear = new Date().getFullYear(), requestedBy = "", requestedByRole = "Employee" } = {}) {
  ensureFlowUrl(HR_LEAVE_ENSURE_BALANCES_FLOW_URL, "Leave Management EnsureLeaveBalances");
  if (!employeeCode) throw new Error("EmployeeCode is required to initialize leave balances.");

  const response = await fetch(HR_LEAVE_ENSURE_BALANCES_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      employeeCode,
      leaveYear,
      requestedBy,
      requestedByRole,
    }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to initialize leave balances");

  return {
    success: data?.success !== false,
    message: data?.message || "Leave balances initialized successfully.",
    employeeCode: data?.employeeCode || employeeCode,
    leaveYear: toNumber(data?.leaveYear, leaveYear),
    assignment: normalizeAssignment(data?.assignment || {}),
    leavePolicyRules: parseFlowArray(data?.leavePolicyRules).map(normalizeLeavePolicyRule),
    leaveBalances: parseFlowArray(data?.leaveBalances).map(normalizeLeaveBalance),
  };
}

// =====================================================
// Get Leave Management Data
// =====================================================
export async function getLeaveManagementData({
  employeeCode = "",
  role = "Employee",
  viewMode = "self",
  legalEntityCode = "",
  locationCode = "",
  departmentCode = "",
  workUnitCode = "",
  managerEmployeeCode = "",
  includeInactive = false,
} = {}) {
  ensureFlowUrl(HR_LEAVE_GETDATA_FLOW_URL, "Leave Management GetData");
  const response = await fetch(HR_LEAVE_GETDATA_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      employeeCode,
      role,
      viewMode,
      legalEntityCode,
      locationCode,
      departmentCode,
      workUnitCode,
      managerEmployeeCode,
      includeInactive,
    }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to load leave management data");

  return {
    success: data?.success !== false,
    viewMode: data?.viewMode || viewMode,
    role: data?.role || role,
    employeeCode: data?.employeeCode || employeeCode,
    employee: data?.employee || null,
    employeeName: extractChoiceValue(data?.employeeName || data?.EmployeeName || data?.employee?.employeeName || data?.employee?.EmployeeName || data?.employee?.fullName || data?.employee?.FullName || data?.employee?.firstName || data?.employee?.FirstName),
    leaveRequests: parseFlowArray(data?.leaveRequests).map(normalizeLeaveRequest),
    approvalRequests: parseFlowArray(data?.approvalRequests).map(normalizeLeaveRequest),
    leaveBalances: parseFlowArray(data?.leaveBalances).map(normalizeLeaveBalance),
    leaveActivityLog: parseFlowArray(data?.leaveActivityLog).map(normalizeLeaveActivityLog),
    approvalActivityLog: parseFlowArray(data?.approvalActivityLog).map(normalizeLeaveActivityLog),
  };
}

// =====================================================
// Apply Leave Request
// =====================================================
export async function applyLeaveRequest({ employeeCode, employeeName = "", leaveType, fromDate, toDate, reason, actionBy = "", actionByRole = "Employee" } = {}) {
  ensureFlowUrl(HR_LEAVE_ACTION_FLOW_URL, "Leave Management Action");
  const response = await fetch(HR_LEAVE_ACTION_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      action: "apply",
      employeeCode,
      employeeName,
      leaveType,
      fromDate,
      toDate,
      reason,
      actionBy: actionBy || employeeCode,
      actionByRole,
    }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Leave request submission failed");
  return data;
}

// =====================================================
// Status Actions
// action values: approve, reject, cancelPending, requestCancellation,
// approveCancellation, rejectCancellation
// =====================================================
export async function updateLeaveRequestStatus({ id, action, actionBy = "", actionByRole = "Manager", remarks = "" } = {}) {
  ensureFlowUrl(HR_LEAVE_ACTION_FLOW_URL, "Leave Management Action");
  if (!id) throw new Error("Leave request ID is required.");
  if (!action) throw new Error("Leave action is required.");

  const response = await fetch(HR_LEAVE_ACTION_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({

      action,
      id: String(id),
      actionBy,
      actionByRole,
      remarks,
    }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Leave status update failed");
  return data;
}

export default {
  ensureLeaveBalances,
  getLeaveManagementData,
  applyLeaveRequest,
  updateLeaveRequestStatus,
};
